import { toast } from "vue3-toastify";
import { IdContato, IdEtiqueta, IdInbox } from "../common-types";
import { ResponseEtiqueta } from "../etiquetas/etiquetas.service";
import { GenericServiceBase } from "../generic-service";

export type CreateContatoEtiqueta = {
    idContato : IdContato;
    idEtiqueta: IdEtiqueta;
    idAtendimento?: string; 
    idUsuario    ?: string; 
    idInbox      ?: string;
}

export type VincularLoteContatosEtiquetas = {
    idContatos : IdContato[];
    idEtiquetas: IdEtiqueta[];
    idInbox   ?: IdInbox;
}

/**
 * Alvo da remoção: uma lista de etiquetas OU `todasDoInbox` — nunca os dois.
 * `todasDoInbox` limpa as etiquetas do escopo do inbox (as dele e as gerais),
 * não todas as do contato.
 */
export type RemoverLoteContatosEtiquetas = {
    idContatos    : IdContato[];
    idEtiquetas  ?: IdEtiqueta[];
    todasDoInbox ?: boolean;
    idInbox      ?: IdInbox;
}

export type ResponseVinculoLote = {
    vinculosCriados  : number;
    vinculosIgnorados: number;
    contatos         : number;
    etiquetas        : number;
}

export type ResponseRemocaoLote = {
    vinculosRemovidos: number;
    contatos         : number;
    etiquetas        : number;
}

export type ResponseContatoEtiqueta = {
    id        : string;
    idContato : IdContato;
    idEtiqueta: IdEtiqueta;
}

export class ContatoEtiquetaService extends GenericServiceBase {
    constructor() { super('contatos-etiquetas') }

    async create(etiqueta: CreateContatoEtiqueta) { return await this.f.post('', etiqueta) }
    
    async getAllLinks() { return await this.f.get('') as ResponseContatoEtiqueta[] }
    
    async getEtiquetasByContato(idContato: IdContato) { 
        try {
            return await this.f.get(`/${idContato}`) as ResponseEtiqueta[] 
        } catch (e) {
            console.error(e);
            toast.error((e as Error).message || `Não foi possível recuperar as etiquetas.`);
            return [];
        }
    }
    
    async getCountByEtiqueta(idEtiqueta: IdEtiqueta): Promise<number> {
        try {
            const res = await this.f.get(`/count/${idEtiqueta}`) as { count: number };
            return res.count ?? 0;
        } catch {
            return 0;
        }
    }

    async sync(idContato: IdContato, idEtiquetas: IdEtiqueta[]) {
        return await this.f.put(`/${idContato}`, { idEtiquetas }) as ResponseContatoEtiqueta[]
    }

    /**
     * Ação em lote "Etiquetar": um POST para todos os pares contato↔etiqueta.
     * Pares que o contato já possui são ignorados pelo servidor (a PK da tabela
     * é o par), então não há erro de duplicidade a tratar aqui.
     */
    async vincularEmLote(payload: VincularLoteContatosEtiquetas) {
        return await this.f.post('/lote', payload) as ResponseVinculoLote
    }

    /**
     * Ação em lote "Etiquetar", modo Remover. Contato que não possui a etiqueta
     * é ignorado pelo servidor — o retorno conta só os vínculos que existiam.
     */
    async removerEmLote(payload: RemoverLoteContatosEtiquetas) {
        return await this.f.post('/lote/remover', payload) as ResponseRemocaoLote
    }

    async remove(idContato: IdContato, idEtiqueta: IdEtiqueta) { return await this.f.delete(`/${idContato}/${idEtiqueta}`) }
}